/**
 * Message Service
 * Handles messaging, threading, and notifications
 */

import { Message, MessageFormData, MessageStatus } from '@/types'
import { BaseService } from './base.service'
import { StorageKeys } from './storage'
import {
  cleanUser,
  cleanUsers,
  cleanWorkspace,
  cleanTasks,
  cleanProjects,
  cleanDocuments,
  cleanMessage,
} from '@/lib/utils/clean-references'

class MessageService extends BaseService<Message> {
  constructor() {
    super(StorageKeys.MESSAGES)
  }

  /**
   * Create message with all relationships
   */
  async createMessage(
    data: MessageFormData,
    creatorId: string,
    status: MessageStatus = MessageStatus.Published
  ): Promise<Message> {
    const { userService } = await import('./user.service')
    const { workspaceService } = await import('./workspace.service')
    const { taskService } = await import('./task.service')
    const { projectService } = await import('./project.service')
    const { documentService } = await import('./document.service')

    const creator = await userService.getById(creatorId)
    if (!creator) {
      throw new Error('Creator not found')
    }

    // Workspace is now optional
    let workspace = undefined
    if (data.workspaceId) {
      workspace = await workspaceService.getById(data.workspaceId)
      if (!workspace) {
        throw new Error('Workspace not found')
      }
    }

    const users = await userService.getByIds(data.userIds)
    const tasks = data.taskIds ? await taskService.getByIds(data.taskIds) : []
    const projects = data.projectIds ? await projectService.getByIds(data.projectIds) : []
    const documents = data.documentIds ? await documentService.getByIds(data.documentIds) : []

    // Clean all references to prevent circular reference issues
    return this.create({
      creator: cleanUser(creator),
      status,
      name: data.name,
      description: data.description,
      directMessage: data.directMessage,
      parentId: data.parentId,
      workspace: workspace ? cleanWorkspace(workspace) : undefined,
      tasks: cleanTasks(tasks),
      users: cleanUsers(users),
      documents: cleanDocuments(documents),
      projects: cleanProjects(projects),
      messages: [], // Threaded messages
      readBy:
        status === MessageStatus.Published
          ? [
              {
                userId: creatorId,
                readAt: new Date(), // Creator automatically reads their own message
              },
            ]
          : [], // Drafts don't have read status yet
    })
  }

  /**
   * Update message with all relationships (only drafts can be updated)
   */
  async updateMessage(messageId: string, data: MessageFormData, userId: string): Promise<Message> {
    const { userService } = await import('./user.service')
    const { workspaceService } = await import('./workspace.service')
    const { taskService } = await import('./task.service')
    const { projectService } = await import('./project.service')
    const { documentService } = await import('./document.service')

    const message = await this.getById(messageId)
    if (!message) {
      throw new Error('Message not found')
    }

    // Only creator can update
    if (message.creator.id !== userId) {
      throw new Error('Only the message creator can update this message')
    }

    // Only drafts can be updated
    if (message.status !== MessageStatus.Draft) {
      throw new Error('Only draft messages can be updated')
    }

    // Workspace is now optional
    let workspace = undefined
    if (data.workspaceId) {
      workspace = await workspaceService.getById(data.workspaceId)
      if (!workspace) {
        throw new Error('Workspace not found')
      }
    }

    const users = await userService.getByIds(data.userIds)
    const tasks = data.taskIds ? await taskService.getByIds(data.taskIds) : []
    const projects = data.projectIds ? await projectService.getByIds(data.projectIds) : []
    const documents = data.documentIds ? await documentService.getByIds(data.documentIds) : []

    // Clean all references to prevent circular reference issues
    return this.update(messageId, {
      name: data.name,
      description: data.description,
      directMessage: data.directMessage,
      workspace: workspace ? cleanWorkspace(workspace) : undefined,
      tasks: cleanTasks(tasks),
      users: cleanUsers(users),
      documents: cleanDocuments(documents),
      projects: cleanProjects(projects),
    } as Partial<Message>)
  }

  /**
   * Get messages for workspace
   */
  async getMessagesForWorkspace(workspaceId: string): Promise<Message[]> {
    return this.filter((message) => message.workspace?.id === workspaceId)
  }

  /**
   * Get messages for user (includes messages where user is creator or recipient)
   */
  async getMessagesForUser(userId: string): Promise<Message[]> {
    return this.filter(
      (message) =>
        message.creator.id === userId || // Messages created by user
        message.users.some((u) => u.id === userId) // Messages where user is a recipient
    )
  }

  /**
   * Get direct messages between users
   */
  async getDirectMessages(userId1: string, userId2: string): Promise<Message[]> {
    return this.filter(
      (message) =>
        message.directMessage &&
        message.users.length === 2 &&
        message.users.some((u) => u.id === userId1) &&
        message.users.some((u) => u.id === userId2)
    )
  }

  /**
   * Get group messages for user
   */
  async getGroupMessages(userId: string): Promise<Message[]> {
    return this.filter(
      (message) => !message.directMessage && message.users.some((u) => u.id === userId)
    )
  }

  /**
   * Get messages by status
   */
  async getMessagesByStatus(status: MessageStatus): Promise<Message[]> {
    return this.filter((message) => message.status === status)
  }

  /**
   * Get threaded messages (replies)
   */
  async getThreadedMessages(parentMessageId: string): Promise<Message[]> {
    const parent = await this.getById(parentMessageId)
    return parent?.messages || []
  }

  /**
   * Get message with full reply thread (refreshed from storage)
   */
  async getMessageWithReplies(messageId: string): Promise<Message | null> {
    return this.getById(messageId)
  }

  /**
   * Helper: Find and update a nested message within a message tree
   */
  private updateNestedMessage(
    messages: Message[],
    targetId: string,
    updater: (msg: Message) => Message
  ): { messages: Message[]; found: boolean } {
    let found = false

    const updatedMessages = messages.map((msg) => {
      if (msg.id === targetId) {
        found = true
        return updater(msg)
      }

      if (msg.messages && msg.messages.length > 0) {
        const result = this.updateNestedMessage(msg.messages, targetId, updater)
        if (result.found) {
          found = true
          return { ...msg, messages: result.messages }
        }
      }

      return msg
    })

    return { messages: updatedMessages, found }
  }

  /**
   * Helper: Find the root parent message for any nested message
   */
  private async findRootParent(messageId: string): Promise<Message | null> {
    const allMessages = await this.getAll()

    // Check if this message itself is a root (not nested in any other message)
    const isRoot = !allMessages.some((msg) => this.messageExistsInTree(msg.messages, messageId))

    if (isRoot) {
      return this.getById(messageId)
    }

    // Find the root message that contains this nested message
    for (const msg of allMessages) {
      if (this.messageExistsInTree(msg.messages, messageId)) {
        return msg
      }
    }

    return null
  }

  /**
   * Find the immediate parent message for a given message
   * Returns null if the message is a root message (not a reply)
   */
  async findParentMessage(messageId: string): Promise<Message | null> {
    const allMessages = await this.getAll()

    // Check each message's direct children
    for (const msg of allMessages) {
      if (msg.messages.some((reply) => reply.id === messageId)) {
        return msg
      }

      // Check nested messages recursively
      const parent = this.findParentInTree(msg.messages, messageId)
      if (parent) {
        return parent
      }
    }

    return null
  }

  /**
   * Helper: Recursively search for the immediate parent in a message tree
   */
  private findParentInTree(messages: Message[], targetId: string): Message | null {
    for (const msg of messages) {
      // Check if this message is the direct parent
      if (msg.messages.some((reply) => reply.id === targetId)) {
        return msg
      }

      // Continue searching in nested messages
      if (msg.messages && msg.messages.length > 0) {
        const parent = this.findParentInTree(msg.messages, targetId)
        if (parent) {
          return parent
        }
      }
    }

    return null
  }

  /**
   * Helper: Check if a message exists anywhere in a message tree
   */
  private messageExistsInTree(messages: Message[], targetId: string): boolean {
    for (const msg of messages) {
      if (msg.id === targetId) return true
      if (msg.messages && this.messageExistsInTree(msg.messages, targetId)) return true
    }
    return false
  }

  /**
   * Reply to message (add to thread) - handles both root and nested messages
   * Replies automatically inherit associations from their parent message
   */
  async replyToMessage(
    parentMessageId: string,
    replyData: MessageFormData,
    creatorId: string
  ): Promise<{ reply: Message; updatedParent: Message }> {
    const parent = await this.getById(parentMessageId)
    if (!parent) {
      throw new Error('Parent message not found')
    }

    // Ensure reply inherits all associations from parent if not explicitly provided
    const inheritedReplyData: MessageFormData = {
      ...replyData,
      parentId: parentMessageId, // Set parent ID for threading
      taskIds: replyData.taskIds ?? parent.tasks.map((t) => t.id),
      projectIds: replyData.projectIds ?? parent.projects.map((p) => p.id),
      documentIds: replyData.documentIds ?? parent.documents.map((d) => d.id),
    }

    const reply = await this.createMessage(inheritedReplyData, creatorId)

    // Find the root parent message
    const rootParent = await this.findRootParent(parentMessageId)

    if (!rootParent) {
      throw new Error('Root parent message not found')
    }

    // If replying to the root message directly
    if (rootParent.id === parentMessageId) {
      const updatedParent = await this.update(parentMessageId, {
        messages: [...parent.messages, cleanMessage(reply)],
      } as Partial<Message>)
      return { reply, updatedParent }
    }

    // If replying to a nested message, update the nested message within the root's tree
    const result = this.updateNestedMessage(rootParent.messages, parentMessageId, (msg) => ({
      ...msg,
      messages: [...(msg.messages || []), cleanMessage(reply)],
    }))

    if (!result.found) {
      throw new Error('Target message not found in tree')
    }

    // Update the root parent with the modified tree
    const updatedRoot = await this.update(rootParent.id, {
      messages: result.messages,
    } as Partial<Message>)

    return { reply, updatedParent: updatedRoot }
  }

  /**
   * Archive message
   */
  async archiveMessage(messageId: string): Promise<Message> {
    return this.update(messageId, { status: MessageStatus.Archived } as Partial<Message>)
  }

  /**
   * Unarchive message
   */
  async unarchiveMessage(messageId: string): Promise<Message> {
    return this.update(messageId, { status: MessageStatus.Published } as Partial<Message>)
  }

  /**
   * Delete message (soft delete)
   */
  async deleteMessage(messageId: string): Promise<Message> {
    return this.update(messageId, { status: MessageStatus.Deleted } as Partial<Message>)
  }

  /**
   * Search messages
   */
  async searchMessages(query: string): Promise<Message[]> {
    return this.search(query, ['name', 'description'])
  }

  /**
   * Get unread messages for user
   */
  async getUnreadMessages(userId: string): Promise<Message[]> {
    const messages = await this.getMessagesForUser(userId)
    return messages.filter(
      (m) =>
        m.status === MessageStatus.Published && !m.readBy.some((read) => read.userId === userId)
    )
  }

  /**
   * Get read messages for user
   */
  async getReadMessages(userId: string): Promise<Message[]> {
    const messages = await this.getMessagesForUser(userId)
    return messages.filter(
      (m) => m.status === MessageStatus.Published && m.readBy.some((read) => read.userId === userId)
    )
  }

  /**
   * Check if a message is read by a specific user
   */
  isMessageReadByUser(message: Message, userId: string): boolean {
    return message.readBy.some((read) => read.userId === userId)
  }

  /**
   * Mark message as read
   */
  async markAsRead(messageId: string, userId: string): Promise<Message> {
    const message = await this.getById(messageId)
    if (!message) {
      throw new Error('Message not found')
    }

    // Check if already read
    if (this.isMessageReadByUser(message, userId)) {
      return message
    }

    // Add read status
    const updatedReadBy = [
      ...message.readBy,
      {
        userId,
        readAt: new Date(),
      },
    ]

    return this.update(messageId, { readBy: updatedReadBy } as Partial<Message>)
  }

  /**
   * Mark message as unread
   */
  async markAsUnread(messageId: string, userId: string): Promise<Message> {
    const message = await this.getById(messageId)
    if (!message) {
      throw new Error('Message not found')
    }

    // Remove read status for this user
    const updatedReadBy = message.readBy.filter((read) => read.userId !== userId)

    return this.update(messageId, { readBy: updatedReadBy } as Partial<Message>)
  }

  /**
   * Mark all messages as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    const unreadMessages = await this.getUnreadMessages(userId)
    await Promise.all(unreadMessages.map((message) => this.markAsRead(message.id, userId)))
  }
}

export const messageService = new MessageService()
